import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, PackageMinus, Search } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, TH } from "@/components/common";
import { KitDialog } from "@/components/KitDialog";
import { api, apiError, fmtQty } from "@/lib/api";

function KitCard({ k, onOpen }) {
  const zero = k.buildable === 0;
  return (
    <button type="button" onClick={onOpen} data-testid={`kit-card-${k.code}`}
      className="group flex flex-col rounded-md border bg-white p-5 text-left shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-md sk-rise">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-sm font-semibold text-slate-500">{k.code}</div>
          <div className="font-display text-2xl font-bold uppercase leading-tight text-slate-900">{k.designation}</div>
        </div>
        <div className={`rounded px-3 py-2 text-center ${zero ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
          <div className="font-mono text-2xl font-bold leading-none" data-testid={`kit-card-buildable-${k.code}`}>{k.buildable}</div>
          <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider">réalisables</div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {k.components.map((c) => (
          <span key={c.code} className={`rounded border px-2 py-1 font-mono text-xs ${c.code === k.limiting ? "border-orange-300 bg-orange-50 text-orange-800" : "bg-slate-50 text-slate-600"}`}>{fmtQty(c.qty)}× {c.code}</span>
        ))}
      </div>
      <div className="mt-3 text-xs text-slate-500">Composant limitant : <span className="font-mono font-semibold text-slate-700">{k.limiting}</span></div>
    </button>
  );
}

function KitDetail({ kit, onClose, onEdit, onDelete }) {
  const navigate = useNavigate();
  return (
    <Dialog open={!!kit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="kit-detail-dialog">
        {kit && <>
          <DialogHeader>
            <DialogTitle className="font-display text-3xl uppercase">Kit {kit.code} — {kit.designation}</DialogTitle>
            <DialogDescription>Kit virtuel sans stock propre. <span className="font-semibold text-slate-900" data-testid="kit-detail-buildable">{kit.buildable} kit(s) réalisable(s)</span> — composant limitant <span className="font-mono font-semibold">{kit.limiting}</span>.</DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto rounded border">
            <Table data-testid="kit-detail-table">
              <TableHeader><TableRow><TableHead className={TH}>Article</TableHead><TableHead className={`${TH} text-right`}>Qté / kit</TableHead><TableHead className={`${TH} text-right`}>Stock</TableHead><TableHead className={`${TH} text-right`}>Kits possibles</TableHead></TableRow></TableHeader>
              <TableBody>
                {kit.components.map((c) => (
                  <TableRow key={c.code} className={c.code === kit.limiting ? "bg-orange-50" : ""} data-testid={`kit-detail-row-${c.code}`}>
                    <TableCell><span className="font-mono font-semibold">{c.code}</span> — {c.designation}{c.code === kit.limiting && <span className="ml-2 rounded bg-orange-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-orange-900">Limitant</span>}</TableCell>
                    <TableCell className="text-right font-mono">{fmtQty(c.qty)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtQty(c.stock)} <span className="text-xs text-slate-400">{c.unite}</span></TableCell>
                    <TableCell className="text-right font-mono font-semibold">{c.possible}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" className="h-12 gap-2 text-rose-600" onClick={onDelete} data-testid="kit-delete-button"><Trash2 className="h-4 w-4" /> Supprimer</Button>
            <Button variant="outline" className="h-12 gap-2" onClick={onEdit} data-testid="kit-edit-button"><Pencil className="h-4 w-4" /> Modifier</Button>
            <Button className="h-12 gap-2 bg-orange-500 text-slate-950 hover:bg-orange-400" onClick={() => navigate(`/mouvements?type=sortie_kit&code=${encodeURIComponent(kit.code)}`)} data-testid="kit-exit-button"><PackageMinus className="h-4 w-4" /> Sortir des kits</Button>
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
}

export default function Kits() {
  const [kits, setKits] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [dialog, setDialog] = useState({ open: false, kit: null });

  const load = () => api.get("/kits").then((r) => setKits(r.data));
  useEffect(() => { load(); }, []);

  const remove = async () => {
    if (!window.confirm(`Supprimer le kit ${detail.code} ?`)) return;
    try {
      await api.delete(`/kits/${encodeURIComponent(detail.code)}`);
      toast.success("Kit supprimé");
      setDetail(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const shown = kits.filter((k) => `${k.code} ${k.designation}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="kits-page">
      <PageHeader title="Kits virtuels" subtitle="Compositions d'articles simples. La sortie d'un kit décrémente ses composants." testid="kits-title">
        <Button className="h-12 gap-2" onClick={() => setDialog({ open: true, kit: null })} data-testid="kits-new-button"><Plus className="h-4 w-4" /> Nouveau kit</Button>
      </PageHeader>
      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un kit…" className="h-12 bg-white pl-10" data-testid="kits-search-input" />
      </div>
      {shown.length === 0 && <p className="text-slate-500" data-testid="kits-empty">Aucun kit.</p>}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {shown.map((k) => <KitCard key={k.code} k={k} onOpen={() => setDetail(k)} />)}
      </div>
      <KitDetail kit={detail} onClose={() => setDetail(null)} onDelete={remove} onEdit={() => { setDialog({ open: true, kit: detail }); setDetail(null); }} />
      <KitDialog open={dialog.open} kit={dialog.kit} onOpenChange={(o) => setDialog({ ...dialog, open: o })} onSaved={load} />
    </div>
  );
}
